import { Box, Link, useTheme } from '@chakra-ui/react';
import styled from '@emotion/styled/macro';
import { FC } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { mq } from 'styles/breakpoints';
import { rgba } from 'polished';
import { useSelector } from 'react-redux';
import { isGuestContextSelector, isCorporateThemeSelector } from 'modules/app/appSlice';
import { rem } from 'utils/fonts';
import { tags, setTag } from 'utils/tags';

const activeClassName = 'nav-item-active';

const StyledBox = styled(Box)`
  overflow: hidden;
  height: 100%;
  position: relative;
`;

const Wrapper = styled(Box)`
  overflow: hidden;
  overflow-x: auto;
  white-space: nowrap;
  padding: 0 26px 20px 0;
  height: calc(100% + 20px);

  &::after {
    content: '';
    position: absolute;
    right: 0;
    bottom: 0;
    top: 0;
    width: 30px;
  }
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  height: 100%;
`;

const Item = styled.li`
  display: inline-flex;
  margin: 0 18px;
  height: 100%;
  align-items: center;

  ${mq.xl} {
    margin: 0 20px;
  }
`;

const activeLinkCorporate = `
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  height: 40px;
  width: calc(100% + 32px);
`;

const activeLink = `
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
`;

const NavItem = styled(Link)<{ isCorporateTheme: boolean }>`
  display: inline-flex;
  position: relative;
  padding: 0 2px;
  height: 100%;
  align-items: center;
  outline: none;
  transition: all 0.2s ease-in-out;

  &:after {
    content: '';
    position: absolute;
    opacity: 0;
    border-radius: 4px;
    transition: all 0.2s ease-in-out;
    ${({ isCorporateTheme }) => (isCorporateTheme ? activeLinkCorporate : activeLink)}
  }

  &.${activeClassName} {
    &:after {
      opacity: ${({ isCorporateTheme }) => (isCorporateTheme ? 0.15 : 1)};
    }
  }
`;

interface Props {
  items: MenuItem[];
}

const NavBar: FC<Props> = ({ items }) => {
  const { colors } = useTheme();
  const location = useLocation();
  const isGuest = useSelector(isGuestContextSelector);
  const isCorporateTheme = useSelector(isCorporateThemeSelector);

  const setGoogleTag = (id: string): void => {
    const isNotInOffset = !window.location.pathname.includes('/offset');
    if (id === 'offset' && isNotInOffset) {
      setTag(tags.portalOffset.entry);
    }
  };

  return (
    <StyledBox
      as="nav"
      display={isCorporateTheme ? { base: 'none', xl: 'block' } : { base: 'none', lg: 'block' }}
      width={{ sm: 'full', md: 'auto' }}
      data-testid="navbar"
    >
      <Wrapper
        sx={isCorporateTheme ? undefined : { '&::after': { bg: `linear-gradient(90deg, ${rgba(colors.white, 0.2)} 0%, ${colors.white} 90%)` } }}
      >
        <List>
          {items.map((item: MenuItem) => (
            <Item key={item.text}>
              {item.hasAccess ? (
                <NavItem
                  as={NavLink}
                  exact
                  to={{ pathname: item.link, search: location.search }}
                  activeClassName={activeClassName}
                  isCorporateTheme={isCorporateTheme}
                  fontSize={rem(16)}
                  isActive={
                    isGuest
                      ? undefined
                      : //@ts-ignore
                        (_, location): boolean => {
                          const pathBase = location.pathname.split('/')[1];
                          const linkBase = item.link.split('/')[1];
                          return pathBase === linkBase;
                        }
                  }
                  sx={{
                    color: isCorporateTheme ? rgba(colors.white, 0.9) : rgba(colors.gray[900], 0.5),
                    '&:hover': {
                      textDecoration: 'none',
                    },
                    '&::after': {
                      bg: isCorporateTheme ? colors.white : colors.other.CTA,
                    },
                    [`&.${activeClassName}`]: {
                      color: isCorporateTheme ? rgba(colors.white, 0.9) : colors.gray[900],
                      fontWeight: isCorporateTheme ? 500 : 400,
                    },
                  }}
                  onClick={() => setGoogleTag(item.id)}
                >
                  <span id={`${item.id}-link`} className={`${item.id}-link`}>
                    {item.text}
                  </span>
                </NavItem>
              ) : (
                <Box
                  as="span"
                  color={isCorporateTheme ? rgba(colors.secondary[400], 0.5) : rgba(colors.gray[900], 0.2)}
                  fontSize={isCorporateTheme ? rem(13) : rem(16)}
                >
                  {item.text}
                </Box>
              )}
            </Item>
          ))}
        </List>
      </Wrapper>
    </StyledBox>
  );
};

export default NavBar;
